import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  PreviewSnapshotDto,
  PublicationClient,
  PublishInput,
  PublishResultDto,
  UnpublishInput,
  UnpublishResultDto,
  ValidateAnalysisResultDto,
} from '@/api/publication-client';
import { usePublicationClient } from '@/api/publication-client-context';

// =============================================================================
// use-publication.ts — Hooks TanStack Query de la publication ADMIN (S18 / stream C).
// =============================================================================
// Lecture (aperçu anonymisé) en useQuery ; commandes (valider / publier) en useMutation (effets de bord
// serveur + transition d'état). Le client effectif vient du PublicationClientProvider (doublable, stub-first).
//
// ZÉRO règle métier ici : l'autorité (anonymisation des bands, transitions Disponible→Validee→Publiee,
// irréversibilité, exigence du double-confirm) est l'API/worker. Après VALIDATION ou PUBLICATION, on INVALIDE
// l'aperçu pour refléter le nouvel `analysisState` serveur (état machine qui gouverne l'activation des boutons,
// distinct du `publicationState` du snapshot). L'erreur API (PublicationApiError, y compris 409 double-confirm/
// mauvais état/déjà publié) remonte telle quelle.

const PUBLICATION_KEY = ['publication'] as const;

/** Clé de query de l'aperçu anonymisé (snapshot) d'une campagne. */
export function previewKey(campaignId: string): readonly unknown[] {
  return [...PUBLICATION_KEY, 'preview', campaignId];
}

/** GET aperçu ANONYMISÉ (bands p25/p75/suppressed + métadonnées de traçabilité, décision Cx-S18-5). */
export function usePublicationPreview(
  campaignId: string,
): UseQueryResult<PreviewSnapshotDto, Error> {
  const client: PublicationClient = usePublicationClient();
  return useQuery<PreviewSnapshotDto, Error>({
    queryKey: previewKey(campaignId),
    queryFn: () => client.getPreview(campaignId),
  });
}

/** VALIDER l'analyse (Disponible → Validee). Invalide l'aperçu (le nouvel état débloque la publication). */
export function useValidateAnalysis(
  campaignId: string,
): UseMutationResult<ValidateAnalysisResultDto, Error, void> {
  const client: PublicationClient = usePublicationClient();
  const qc = useQueryClient();
  return useMutation<ValidateAnalysisResultDto, Error, void>({
    mutationFn: () => client.validateAnalysis(campaignId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: previewKey(campaignId) });
    },
  });
}

/**
 * PUBLIER le benchmark (Validee → Publiee, IRRÉVERSIBLE — décision PP-08). Le body porte le DOUBLE confirm
 * ({confirm, confirmAgain}) ; l'API répond 409 si l'un manque. Invalide l'aperçu (l'état passe à Publiee).
 */
export function usePublish(
  campaignId: string,
): UseMutationResult<PublishResultDto, Error, PublishInput> {
  const client: PublicationClient = usePublicationClient();
  const qc = useQueryClient();
  return useMutation<PublishResultDto, Error, PublishInput>({
    mutationFn: (input) => client.publish(campaignId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: previewKey(campaignId) });
    },
  });
}

/**
 * DÉ-PUBLIER le benchmark (publicationState Published → Draft, DBU §2.6 l.1450). Le body porte le `motif`
 * OBLIGATOIRE ; l'API répond 400 s'il est vide, 409 si l'analyse n'est pas publiée / conflit, 404 si aucune
 * analyse. Invalide l'aperçu au succès : le backend flippe SEULEMENT `publicationState` (retrait de la
 * visibilité Pulse) et LAISSE `analysisState` inchangé (reste `Publiee`, DEC-39). Le badge « Publié » disparaît.
 */
export function useUnpublish(
  campaignId: string,
): UseMutationResult<UnpublishResultDto, Error, UnpublishInput> {
  const client: PublicationClient = usePublicationClient();
  const qc = useQueryClient();
  return useMutation<UnpublishResultDto, Error, UnpublishInput>({
    mutationFn: (input) => client.unpublish(campaignId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: previewKey(campaignId) });
    },
  });
}
